import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import Textarea from 'react-textarea-autosize';
import {
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Label,
  Input,
  FormText,
  Row,
  Button,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { isMini } from '../../helpers/App';
import { getTaxonomyRoleSetting } from '../../helpers/Metadata';
import { userHasFeature } from '../../helpers/Features';
import { getSetting } from '../../helpers/Settings';

import UploadFileTaxonomy from './FileTaxonomy';
import UploadFileCustomField from './FileCustomField';
import UploadFileCollection from './FileCollectionV2';
import UploadFileAccess from './FileAccess';
import UploadFileDate from './FileDate';
import UploadFileExpirationDate from './FileExpirationDate';
import UploadFileAccessGroups from './FileAccessGroups';
import FileStatusSelect from './FileStatusSelect';
import FileMetadataGroupPicker from './MetadataGroupPicker';
import AccessModalAccess from '../Access/ModalAccess';
import UpgradePlanButton from '../UpgradePlanButton';

class UploadFileForm extends PureComponent {
  constructor(props) {
    super(props);

    this.form = React.createRef();
    this.fileName = React.createRef();
    this.fileUrl = React.createRef();

    this.handleInsertOriginalFileName = this.handleInsertOriginalFileName.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { selectedFiles } = this.props;
    if (selectedFiles.length !== prevProps.selectedFiles.length) {
      this.resetForm();
    }
  }

  getSelectedFile() {
    const { selectedFiles } = this.props;
    let file = null;
    if (selectedFiles.length === 1) {
      file = selectedFiles[0];
    }
    return file;
  }

  getStatus() {
    const file = this.getSelectedFile();
    const {
      selectedFiles,
      modalMode,
      fileStatuses,
    } = this.props;

    if (modalMode === 'edit' && selectedFiles.length > 1) {
      return { id: -2 };
    }

    if (modalMode === 'upload') {
      const defaultStatus = fileStatuses.find(fs => (fs.default));
      if (defaultStatus) {
        return defaultStatus;
      }
    }

    return (file && file.status) ? file.status : null;
  }

  handleInsertOriginalFileName(e) {
    const { handleFormChange } = this.props;
    const file = this.getSelectedFile();

    e.preventDefault();
    handleFormChange({
      target: {
        name: 'fileName',
        value: file.original_name,
      },
    });
    this.fileName.current.value = file.original_name;
  }

  resetForm() {
    const {
      modalMode,
      selectedFiles,
      handleFormChange,
    } = this.props;

    this.form.current.reset();
    if (!(modalMode === 'edit' && selectedFiles.length > 1)) {
      handleFormChange();
    }
  }

  render() {
    const {
      intl,
      modalMode,
      selectedFiles,
      taxonomies,
      customFields,
      selectedGroups,
      selectedGroupsObject,
      selectedUsersObject,
      accessTypeFromFiles,
      metadataGroups,
      collectionError,
      isLinkEdit,
      showFileAccess,
      handleFormChange,
      handleTaxonomyChange,
      handleMetadataGroupChange,
      handleCollectionChange,
      handleCustomFieldChange,
      handleSwitchGroup,
      handleClearPrefixes,
      handleClearSuffixes,
      existingFiles,
      allowCollectionSelect,
    } = this.props;

    const file = this.getSelectedFile();
    const isEdit = modalMode === 'edit';
    const isLink = modalMode === 'link';

    const getCommonMetadataGroupId = (files) => {
      if (!files || files.length === 0) {
        return null;
      }

      const getGroupId = (group) => {
        if (group && typeof group === 'object') {
          return group.id;
        }
        return group;
      };

      const firstGroupId = getGroupId(files[0].metadata_group);

      const allSame = files.every(f => getGroupId(f.metadata_group) === firstGroupId);

      if (allSame) {
        return firstGroupId;
      }

      return null;
    };

    const metadataGroupToDisplay = getCommonMetadataGroupId(selectedFiles);

    const getTaxonomiesToDisplay = () => {
      const metadataGroup = metadataGroups.find(
        mg => `${mg.id}` === `${metadataGroupToDisplay}`,
      );

      const aiTaxonomies = taxonomies.filter(t => t.ai_tagging);

      const groupedTaxonomies = taxonomies
        .filter(t => !t.ai_tagging)
        .filter(t => metadataGroup?.taxonomies.includes(t.id))
        .sort((a, b) => {
          const order = metadataGroup?.taxonomies || [];
          return order.indexOf(a.id) - order.indexOf(b.id);
        });

      const unassignedTaxonomies = taxonomies.filter(
        t => !t.ai_tagging && t.metadata_groups?.length === 0,
      );

      let result = [];

      if (isEdit) {
        if (metadataGroupToDisplay === 'default') {
          result = [...aiTaxonomies, ...unassignedTaxonomies];
        } else {
          result = [...aiTaxonomies, ...groupedTaxonomies, ...unassignedTaxonomies];
        }
      } else if (metadataGroupToDisplay === null) {
        result = unassignedTaxonomies;
      } else {
        result = [...groupedTaxonomies, ...unassignedTaxonomies];
      }

      if (!isEdit) {
        result = result.filter(t => getTaxonomyRoleSetting(t, 'read_only') !== true);
      }

      return Array.from(new Map(result.map(t => [t.id, t])).values());
    };

    const taxonomiesToDisplay = getTaxonomiesToDisplay();

    const getCustomFieldsToDisplay = () => {
      const unassignedCustomFields = customFields.filter(cf => cf.metadata_groups?.length === 0);

      if (!metadataGroupToDisplay) {
        return unassignedCustomFields;
      }

      if (isEdit) {
        const mainCustomFields = customFields
          .filter(cf => {
            if (!metadataGroupToDisplay) return true;

            if (!cf.metadata_groups || cf.metadata_groups.length === 0) return false;

            const metadataGroup = metadataGroups.find(mg => `${mg.id}` === `${metadataGroupToDisplay}`);

            if (!metadataGroup) return true;

            return metadataGroup.custom_fields.includes(cf.id);
          })
          .sort((a, b) => {
            if (!metadataGroupToDisplay) return 0;

            const metadataGroup = metadataGroups.find(
              mg => `${mg.id}` === `${metadataGroupToDisplay}`,
            );

            if (!metadataGroup) return 0;

            const order = metadataGroup.custom_fields;

            return order.indexOf(a.id) - order.indexOf(b.id);
          });

        return [...mainCustomFields, ...unassignedCustomFields];
      }

      const mainCustomFields = customFields
        .filter(cf => {
          if (!metadataGroupToDisplay) return true;

          if (!cf.metadata_groups || cf.metadata_groups.length === 0) return false;

          const metadataGroup = metadataGroups.find(mg => `${mg.id}` === `${metadataGroupToDisplay}`);

          if (!metadataGroup) return true;

          return metadataGroup.custom_fields.includes(cf.id);
        })
        .sort((a, b) => {
          if (!metadataGroupToDisplay) return 0;

          const metadataGroup = metadataGroups.find(
            mg => `${mg.id}` === `${metadataGroupToDisplay}`,
          );

          if (!metadataGroup) return 0;

          const order = metadataGroup.custom_fields;

          return order.indexOf(a.id) - order.indexOf(b.id);
        });

      return [...mainCustomFields, ...unassignedCustomFields];
    };

    const customFieldsToDisplay = getCustomFieldsToDisplay();

    const haveSelectedFilesPrefixes = !!(selectedFiles.length > 1 &&
      selectedFiles.find(f => f.attributes?.prefix && f.prefix !== ''));
    const haveSelectedFilesSuffixes = !!(selectedFiles.length > 1 &&
      selectedFiles.find(f => f.attributes?.suffix && f.suffix !== ''));

    return (
      <Form
        className="upload-form"
        innerRef={this.form}
        onKeyPress={(e) => {
          const { type } = e.target;
          if (e.key === 'Enter' && type !== 'textarea') {
            e.preventDefault();
          }
        }}
      >
        {isLink && (
          <FormGroup>
            <Label for="inpFileUrl">
              {intl.formatMessage({ defaultMessage: 'Add URL for selected links' })}
            </Label>
            <Input
              type="url"
              name="fileUrl"
              id="inpFileUrl"
              placeholder={selectedFiles.length > 1 ?
                intl.formatMessage({ defaultMessage: 'Select single link card to edit URL' }) :
                intl.formatMessage({ defaultMessage: 'URL' })}
              defaultValue={file ? file.path : ''}
              autoFocus={true}
              innerRef={this.fileUrl}
              disabled={selectedFiles.length > 1}
              onInput={handleFormChange}
              invalid={file && file.uploadStatus === 'invalid'}
            />
            <FormFeedback>
              {intl.formatMessage({ defaultMessage: 'This URL looks incorrect' })}
            </FormFeedback>
          </FormGroup>
        )}
        {isLink && (
          <FormGroup>
            <Label for="inpLinkHtmlCode">
              {intl.formatMessage({ defaultMessage: 'Add embed HTML code' })}
            </Label>
            <Textarea
              className="form-control"
              name="linkHtmlCode"
              id="inpLinkHtmlCode"
              placeholder={intl.formatMessage({ defaultMessage: 'Embed HTML code' })}
              defaultValue={file ? file.html_code : ''}
              onInput={handleFormChange}
            />
          </FormGroup>
        )}
        <FormGroup>
          <Label for="inpFileName">
            {isLink ?
              intl.formatMessage({ defaultMessage: 'Add name for selected links' }) :
              intl.formatMessage({ defaultMessage: 'Add name for selected files' })}
          </Label>
          <Row>
            {((file && file.attributes?.prefix)) && (
              <Col className="form-prefix-suffix-container" sm={3} md={2}>
                <Label for="inpFilePrefix" className="sub-label">
                  {intl.formatMessage({ defaultMessage: 'Prefix' })}
                </Label>
                {file && file.attributes?.prefix && (
                  <Input
                    type="text"
                    name="filePrefix"
                    id="inpFilePrefix"
                    defaultValue={file ? file.attributes.prefix : ''}
                    onInput={handleFormChange}
                  />
                )}
              </Col>
            )}
            <Col>
              {((file && (file.attributes?.prefix || file.attributes?.suffix)) ||
                (haveSelectedFilesPrefixes || haveSelectedFilesSuffixes)) && (
                <Label for="inpFileName" className="sub-label file-name-label">
                  <span>{intl.formatMessage({ defaultMessage: 'File name' })}</span>
                  {/* eslint-disable jsx-a11y/click-events-have-key-events */}
                  {/* eslint-disable jsx-a11y/no-static-element-interactions */}
                  <span>
                    {haveSelectedFilesPrefixes && (
                      <Button color="link" onClick={handleClearPrefixes}>
                        <i className="fal fa-trash" />
                        {intl.formatMessage({ defaultMessage: 'Clear prefixes' })}
                      </Button>
                    )}
                    {haveSelectedFilesSuffixes && (
                      <Button color="link" onClick={handleClearSuffixes} className="ml-3">
                        <i className="fal fa-trash" />
                        {intl.formatMessage({ defaultMessage: 'Clear suffixes' })}
                      </Button>
                    )}
                    {/* eslint-enable jsx-a11y/click-events-have-key-events */}
                    {/* eslint-enable jsx-a11y/no-static-element-interactions */}
                  </span>
                </Label>
              )}
              <Input
                type="text"
                name="fileName"
                id="inpFileName"
                placeholder={isLink ? intl.formatMessage({ defaultMessage: 'Link name' }) :
                  intl.formatMessage({ defaultMessage: 'File name' })}
                defaultValue={file ? file.name : ''}
                innerRef={this.fileName}
                onInput={handleFormChange}
              />
            </Col>
            {((file && file.attributes?.suffix)) && (
              <Col className="form-prefix-suffix-container" sm={3} md={2}>
                <Label for="inpFileSuffix" className="sub-label">
                  {intl.formatMessage({ defaultMessage: 'Suffix' })}
                </Label>
                {file && file.attributes?.suffix && (
                  <Input
                    type="text"
                    name="fileSuffix"
                    id="inpFileSuffix"
                    defaultValue={file ? file.attributes.suffix : ''}
                    onInput={handleFormChange}
                  />
                )}
              </Col>
            )}
          </Row>
          {(selectedFiles.length === 1 && !isLink) && (
            <FormText color="muted">
              {intl.formatMessage({
                defaultMessage: 'Original name: {fileName}',
              }, { fileName: file.original_name })}
              {' '}
              <a
                href="#copy-file-name"
                onClick={this.handleInsertOriginalFileName}
              >
                {intl.formatMessage({ defaultMessage: 'Insert into field' })}
              </a>
            </FormText>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="inpFileDescription">
            {isLink ? intl.formatMessage({ defaultMessage: 'Add description for selected links' }) :
              intl.formatMessage({ defaultMessage: 'Add description for selected files' })}
          </Label>
          <Textarea
            className="form-control"
            name="fileDescription"
            id="inpFileDescription"
            placeholder={isLink ? intl.formatMessage({ defaultMessage: 'Link description' }) :
              intl.formatMessage({ defaultMessage: 'File description' })}
            defaultValue={file ? file.description : ''}
            onInput={handleFormChange}
          />
          <FormFeedback>
            {intl.formatMessage({ defaultMessage: 'Oh noes! Something went wrong.' })}
          </FormFeedback>
        </FormGroup>
        <FileMetadataGroupPicker
          selectedFiles={selectedFiles}
          metadataGroupToDisplay={`${metadataGroupToDisplay}`}
          handleMetadataGroupChange={handleMetadataGroupChange}
        />
        {customFieldsToDisplay.map(customField => (
          <UploadFileCustomField
            key={`custom-field-${customField.id}`}
            customField={customField}
            selectedFiles={selectedFiles}
            file={file}
            handleCustomFieldChange={handleCustomFieldChange}
          />
        ))}
        <FormGroup className="row file-options">
          {!isLink && (
            <Col
              className="file-status-field"
              sm={isMini() ? 6 : 4}
              md={isMini() ? 6 : 4}
              lg={isMini() ? 6 : 3}
            >
              <FileStatusSelect
                defaultValue={this.getStatus()}
                isEdit={(isEdit && selectedFiles.length > 0)}
                handleChange={handleFormChange}
                selectedFile={file}
                selectedFiles={selectedFiles}
                scope="upload"
              />
            </Col>
          )}
          {showFileAccess && (
            <Col sm={6} md={6} lg={isMini() ? 6 : 3}>
              <UploadFileAccess
                isEdit={isEdit}
                isLink={isLink}
                selectedFile={file}
                selectedFiles={selectedFiles}
                handleFormChange={handleFormChange}
              />
            </Col>
          )}
          {(!isLink && !isMini() && userHasFeature('created-date-field')) && (
            <Col sm={6} md={6} lg={isMini() ? 6 : 3}>
              <UploadFileDate
                isEdit={isEdit}
                selectedFile={file}
                selectedFiles={selectedFiles}
                handleFormChange={handleFormChange}
              />
            </Col>
          )}
          {(getSetting('expiration_date') && !isLink) && (
            <Col className="expiration-date-field" sm={6} md={6} lg={isMini() ? 6 : 3}>
              <UploadFileExpirationDate
                isEdit={isEdit}
                selectedFile={file}
                selectedFiles={selectedFiles}
                handleFormChange={handleFormChange}
              />
            </Col>
          )}
        </FormGroup>
        {(!isEdit && !isLinkEdit) && (
          <>
            <UploadFileCollection
              selectedFiles={selectedFiles}
              collectionError={collectionError}
              isLinkEdit={isLinkEdit}
              handleCollectionChange={handleCollectionChange}
              existingFiles={existingFiles}
              allowCollectionSelect={allowCollectionSelect}
            />
            {(getSetting('cs1') && !accessTypeFromFiles.public && showFileAccess) && (
              <UploadFileAccessGroups
                {...this.props}
                selectedGroups={selectedGroups}
                handleSwitchGroup={handleSwitchGroup}
              />
            )}
            {(!getSetting('cs1') && !accessTypeFromFiles.public && showFileAccess) && (
              <AccessModalAccess
                {...this.state}
                {...this.props}
                selectedGroups={selectedGroupsObject}
                selectedUsers={selectedUsersObject}
              />
            )}
          </>
        )}
        {taxonomies.length > 0 && (
          <>
            <hr />
            <div className="upload-modal-taxonomies">
              <h5>
                {isLink ?
                  intl.formatMessage({ defaultMessage: 'Add tags to selected link cards' }) :
                  intl.formatMessage({ defaultMessage: 'Add tags to selected files' })}
              </h5>
              {taxonomiesToDisplay.filter(t => t.standard === true).map(tax => (
                <UploadFileTaxonomy
                  key={`tax-${tax.id}`}
                  taxonomy={tax}
                  selectedFiles={selectedFiles}
                  handleTaxonomyChange={handleTaxonomyChange}
                />
              ))}
              {taxonomiesToDisplay.filter(t => (!(t.ai_tagging || t.standard))).length === 0 ? (
                <h6>
                  <Link to="/admin/settings/categories" target="_blank">
                    {intl.formatMessage({ defaultMessage: 'Create your own Tag Categories' })}
                  </Link>
                  <UpgradePlanButton
                    feature="custom-categories"
                    simple={true}
                  />
                </h6>
              ) : (
                <h5>
                  {intl.formatMessage({ defaultMessage: 'Tag Categories' })}
                </h5>
              )}
              {taxonomiesToDisplay.filter(t => t.standard === false).map(tax => (
                <UploadFileTaxonomy
                  key={`tax-${tax.id}`}
                  taxonomy={tax}
                  selectedFiles={selectedFiles}
                  handleTaxonomyChange={handleTaxonomyChange}
                  isEdit={isEdit}
                />
              ))}
            </div>
          </>
        )}
      </Form>
    );
  }
}

const mapStateToProps = state => ({
  taxonomies: state.Metadata.taxonomies,
  metadataGroups: state.Metadata.metadataGroups,
  customFields: state.Metadata.customFields,
  modalMode: state.Files.uploadModalMode,
  collection: state.Collections.collection,
  collectionUuid: state.Files.collectionUuid,
  fileStatuses: state.Files.statuses,
  isInTrial: state.Auth.profile?.client?.is_in_trial,
});

UploadFileForm.propTypes = {
  intl: PropTypes.object.isRequired,
  taxonomies: PropTypes.array.isRequired,
  metadataGroups: PropTypes.array.isRequired,
  customFields: PropTypes.array.isRequired,
  modalMode: PropTypes.string.isRequired,
  fileStatuses: PropTypes.array.isRequired,
  isInTrial: PropTypes.bool,
  selectedFiles: PropTypes.array.isRequired,
  selectedGroups: PropTypes.array.isRequired,
  selectedUsers: PropTypes.array.isRequired,
  selectedGroupsObject: PropTypes.object.isRequired,
  selectedUsersObject: PropTypes.object.isRequired,
  accessTypeFromFiles: PropTypes.object.isRequired,
  collectionError: PropTypes.bool.isRequired,
  isLinkEdit: PropTypes.bool.isRequired,
  showFileAccess: PropTypes.bool.isRequired,
  handleFormChange: PropTypes.func.isRequired,
  handleTaxonomyChange: PropTypes.func.isRequired,
  handleMetadataGroupChange: PropTypes.func.isRequired,
  handleCollectionChange: PropTypes.func.isRequired,
  handleCustomFieldChange: PropTypes.func.isRequired,
  handleSwitchGroup: PropTypes.func.isRequired,
  handleClearPrefixes: PropTypes.func.isRequired,
  handleClearSuffixes: PropTypes.func.isRequired,
  existingFiles: PropTypes.object,
  allowCollectionSelect: PropTypes.bool,
  metadataGroup: PropTypes.object,
};

UploadFileForm.defaultProps = {
  metadataGroup: null,
  isInTrial: false,
  allowCollectionSelect: true,
  existingFiles: {},
};

export default injectIntl(connect(mapStateToProps)(UploadFileForm));
